/**
 * The bridge message log.
 *
 * `@narrative.io/app-bridge` exposes the data (`onLogEntry`) and deliberately
 * ships no UI, so rendering it is the app's job — this file is the whole of
 * that job. It is here because being able to watch every message crossing the
 * postMessage boundary is the single most useful thing when integrating.
 */

import type { BridgeLogEntry } from '@narrative.io/app-bridge/protocol'

const CAPACITY = 300

/** Bounded buffer, so a long-lived frame cannot grow it without limit. */
export class MessageLog {
	private readonly entries: BridgeLogEntry[] = []
	private subscriber: ((entry: BridgeLogEntry) => void) | undefined

	constructor(private readonly capacity: number = CAPACITY) {}

	/** Pass this straight to `connect({ onLogEntry })`. */
	readonly append = (entry: BridgeLogEntry): void => {
		this.entries.push(entry)
		if (this.entries.length > this.capacity) this.entries.shift()
		this.subscriber?.(entry)
	}

	get size(): number {
		return this.entries.length
	}

	snapshot(): readonly BridgeLogEntry[] {
		return [...this.entries]
	}

	clear(): void {
		this.entries.length = 0
	}

	/** One live view at a time; replaces any previous subscriber. */
	onAppend(subscriber: ((entry: BridgeLogEntry) => void) | undefined): void {
		this.subscriber = subscriber
	}
}

/** `2026-08-04T15:04:05.123Z` → `15:04:05.123`. */
export function formatTime(at: number): string {
	return new Date(at).toISOString().slice(11, 23)
}

/**
 * Messages are untrusted data being put into a log view, so they are truncated
 * and stringified defensively. Rendering uses `textContent` rather than HTML,
 * which is what actually makes this safe.
 */
export function formatMessage(message: unknown, maxLength = 400): string {
	let text: string
	try {
		text = JSON.stringify(message) ?? String(message)
	} catch {
		text = String(message)
	}
	return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text
}
